import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Copy, ExternalLink, Globe, Rocket, Sparkles, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";

import { HelpHint } from "@/components/help";
import { HELP_KEYS } from "@/lib/help-context";
import { useOrganization } from "@/auth/organization";
import {
  DeleteEffect,
  PendingDeletionNotice,
  useDeleteSite,
  useDeletionGrace,
} from "@/components/sites/SiteDeletion";
import {
  DeletionCountdown,
  DeploymentStatusBadge,
  DomainStatusBadge,
  SiteStatusBadge,
} from "@/components/status";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  CopyLinkButton,
  cx,
  EmptyState,
  ErrorAlert,
  ErrorState,
  Field,
  FullPageSpinner,
  Input,
  LinkButton,
  Modal,
  Form,
  Select,
  useMissingPermission,
  useToast,
} from "@/components/ui";
import { portal } from "@/lib/api";
import { config } from "@/lib/config";
import { formatBytes, formatDateTime, siteHref, timeAgo } from "@/lib/format";
import { ApiError } from "@/lib/http";
import { siteKeys } from "@/lib/queries";
import type { CustomDomain, Deployment, DeploymentStatus, Site } from "@/lib/types";
import { choice, useUrlView } from "@/lib/url-state";

const TABS = ["overview", "deployments", "domains", "settings"] as const;
/** How long a disconnected domain can be put back before it is actually removed. */
const UNDO_DISCONNECT_MS = 10_000;
type Tab = (typeof TABS)[number];

const DEPLOYMENT_SORTS = [
  ["newest", "Newest first"],
  ["oldest", "Oldest first"],
  ["status", "Status"],
] as const;
/**
 * The deployment history's sort, kept in the URL next to `?tab=deployments` (PHP-3341): `?sort=`
 * newest first (the default, by deployment number), oldest first, or status (failed, then still
 * building or queued, then unknown, then ready; newest first within each).
 */
const DEPLOYMENT_VIEW = {
  sort: choice(
    "sort",
    DEPLOYMENT_SORTS.map(([s]) => s),
    "newest",
  ),
};
const STATUS_ORDER: Record<DeploymentStatus, number> = {
  failed: 0,
  building: 1,
  queued: 2,
  unknown: 3,
  ready: 4,
};

function sortDeployments(items: Deployment[], sort: (typeof DEPLOYMENT_SORTS)[number][0]) {
  const newest = (a: Deployment, b: Deployment) => b.number - a.number;
  if (sort === "oldest") return [...items].sort((a, b) => a.number - b.number);
  if (sort === "status")
    return [...items].sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || newest(a, b));
  return [...items].sort(newest);
}

export function WebsiteDetailPage() {
  const { orgId } = useOrganization();
  const { siteId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const tab: Tab = (TABS as readonly string[]).includes(params.get("tab") ?? "")
    ? (params.get("tab") as Tab)
    : "overview";
  const site = useQuery({ queryKey: siteKeys.site(orgId, siteId), queryFn: () => portal.site(siteId) });

  if (site.isPending) return <FullPageSpinner />;
  if (site.isError) return <ErrorState error={site.error} onRetry={() => void site.refetch()} />;
  const s = site.data;

  return (
    <>
      <Link
        to="/websites"
        className="mb-4 inline-flex items-center gap-1 text-sm text-fg-muted hover:text-fg"
      >
        <ArrowLeft className="size-4" /> Website
      </Link>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <h1 className="truncate text-2xl font-semibold tracking-tight text-fg">{s.name}</h1>
            <SiteStatusBadge site={s} />
            <DeletionCountdown site={s} />
          </div>
          <a
            href={s.url}
            target="_blank"
            rel="noreferrer"
            className="mt-1 inline-flex items-center gap-1 text-sm text-fg-muted hover:text-fg"
          >
            {s.hostname} <ExternalLink className="size-3.5" />
          </a>
        </div>
        <LinkButton
          to={`${config.builderUrl}/sites/${encodeURIComponent(s.id)}`}
          requires={{ permission: "websites:update", on: ["site", s.id] }}
        >
          <Sparkles className="size-4" /> Edit in builder
        </LinkButton>
      </div>

      {s.status === "pending_deletion" && (
        <div className="mb-6">
          <PendingDeletionNotice site={s} />
        </div>
      )}

      <div className="mb-6 flex gap-1 overflow-x-auto border-b border-border" role="tablist">
        {TABS.map((t) => (
          <Button
            unstyled
            type="submit"
            key={t}
            role="tab"
            aria-selected={tab === t}
            onClick={() => setParams(t === "overview" ? {} : { tab: t }, { replace: true })}
            className={cx(
              "-mb-px border-b-2 px-3 py-2 text-sm font-medium capitalize",
              tab === t ? "border-brand-600 text-fg" : "border-transparent text-fg-muted hover:text-fg",
            )}
          >
            {t}
          </Button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab site={s} />}
      {tab === "deployments" && <DeploymentsTab site={s} />}
      {tab === "domains" && <DomainsTab site={s} />}
      {tab === "settings" && <SettingsTab site={s} />}
    </>
  );
}

function OverviewTab({ site }: { site: Site }) {
  const { orgId } = useOrganization();
  const deployments = useQuery({
    queryKey: siteKeys.deployments(orgId, site.id),
    queryFn: () => portal.deployments(site.id),
  });
  const live = deployments.data?.deployments.find((d) => d.id === site.live_deployment_id);
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader title="Details" />
        <dl className="divide-y divide-border text-sm">
          {[
            [
              "Address",
              <a
                key="a"
                href={site.url}
                target="_blank"
                rel="noreferrer"
                className="text-brand-700 hover:underline dark:text-brand-500"
              >
                {site.url}
              </a>,
            ],
            [
              "Slug",
              <code key="s" className="font-mono text-xs">
                {site.slug}
              </code>,
            ],
            ["Created", formatDateTime(site.created_at)],
            ["Last updated", formatDateTime(site.updated_at)],
          ].map(([k, v]) => (
            <div key={String(k)} className="flex justify-between gap-4 px-5 py-3">
              <dt className="text-fg-muted">{k}</dt>
              <dd className="min-w-0 truncate text-right text-fg">{v}</dd>
            </div>
          ))}
        </dl>
      </Card>
      <Card>
        <CardHeader title="Live version" />
        <div className="px-5 py-4">
          {deployments.isPending ? (
            <FullPageSpinner />
          ) : live ? (
            <div className="space-y-1 text-sm">
              <p className="font-medium text-fg">Deployment #{live.number}</p>
              <p className="text-fg-muted">
                Published {timeAgo(live.finished_at ?? live.created_at)} · {live.file_count} files ·{" "}
                {formatBytes(live.size_bytes)}
              </p>
            </div>
          ) : (
            <EmptyState
              icon={<Rocket className="size-5" />}
              title="Not published yet"
              description="Design your site in the builder and publish it to go live."
            />
          )}
        </div>
      </Card>
    </div>
  );
}

function DeploymentsTab({ site }: { site: Site }) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const [{ sort }, setView] = useUrlView(DEPLOYMENT_VIEW);
  const deployments = useQuery({
    queryKey: siteKeys.deployments(orgId, site.id),
    queryFn: () => portal.deployments(site.id),
    // Keep polling while something is building.
    refetchInterval: (q) =>
      q.state.data?.deployments.some((d) => d.status === "queued" || d.status === "building") ? 3_000 : false,
  });
  const toast = useToast();
  const promote = useMutation({
    mutationFn: (deploymentId: string) => portal.promote(site.id, deploymentId),
    onSuccess: (updated) => {
      toast.show({ tone: "success", title: "Version set live" });
      qc.setQueryData(siteKeys.site(orgId, site.id), updated);
      void qc.invalidateQueries({ queryKey: siteKeys.deployments(orgId, site.id) });
    },
  });
  if (deployments.isPending) return <FullPageSpinner />;
  if (deployments.isError)
    return <ErrorState error={deployments.error} onRetry={() => void deployments.refetch()} />;
  const items = deployments.data.deployments;
  const sorted = sortDeployments(items, sort);
  return (
    <Card>
      <CardHeader
        title={
          <>
            Deployments <HelpHint helpKey={HELP_KEYS.websites.deployments} label="deployments" />
          </>
        }
        description="Every published version. Roll back instantly to any ready deployment."
        action={
          items.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <Select
                aria-label="Sort deployments"
                value={sort}
                onChange={(e) => setView({ sort: e.target.value as typeof sort })}
                className="h-8 w-auto py-1 text-[13px]"
              >
                {DEPLOYMENT_SORTS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
              <CopyLinkButton />
            </div>
          )
        }
      />
      {promote.error && (
        <div className="px-5 pt-4">
          <ErrorAlert error={promote.error} />
        </div>
      )}
      {items.length === 0 ? (
        <EmptyState
          icon={<Rocket className="size-5" />}
          title="No deployments yet"
          description="Deployments appear here when you publish from the builder."
        />
      ) : (
        <ul className="divide-y divide-border">
          {sorted.map((d) => {
            const isLive = d.id === site.live_deployment_id;
            return (
              <li key={d.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-fg">
                    #{d.number}{" "}
                    {isLive && (
                      <span className="ml-1 text-xs font-medium text-brand-700 dark:text-brand-500">
                        ● Live
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-fg-subtle">
                    {formatDateTime(d.created_at)} · {d.file_count} files · {formatBytes(d.size_bytes)}
                  </p>
                  {d.error_message && <p className="mt-1 text-xs text-red-600">{d.error_message}</p>}
                </div>
                <DeploymentStatusBadge status={d.status} />
                {d.status === "ready" && !isLive && (
                  <Button
                    size="sm"
                    variant="secondary"
                    requires={{ permission: "deployments:manage", on: ["site", site.id] }}
                    loading={promote.isPending && promote.variables === d.id}
                    onClick={() => promote.mutate(d.id)}
                  >
                    {d.number < (items.find((x) => x.id === site.live_deployment_id)?.number ?? 0)
                      ? "Roll back"
                      : "Make live"}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function CopyValue({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <code className="min-w-0 truncate rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{value}</code>
      <Button
        unstyled
        type="submit"
        onClick={() => {
          void navigator.clipboard?.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
        className="shrink-0 rounded p-1 text-fg-subtle hover:bg-muted hover:text-fg"
        aria-label={`Copy ${value}`}
      >
        <Copy className="size-3.5" />
      </Button>
      {copied && <span className="text-xs text-brand-700">Copied</span>}
    </span>
  );
}

export function DnsInstructions({ domain }: { domain: CustomDomain }) {
  const v = domain.verification;
  if (!v) return null;
  return (
    <div className="mt-3 space-y-2 rounded-lg border border-border bg-muted/50 p-3 text-xs">
      <p className="text-fg-muted">
        Add these records at your DNS provider, then check again. DNS changes can take up to an hour.
      </p>
      <div className="grid grid-cols-[4rem_1fr] items-center gap-x-3 gap-y-1.5">
        <span className="font-medium text-fg-muted">TXT</span>
        <span className="flex flex-wrap gap-x-3 gap-y-1">
          <CopyValue value={v.txt_record_name} />
          <CopyValue value={v.txt_record_value} />
        </span>
        <span className="font-medium text-fg-muted">CNAME</span>
        <span className="flex flex-wrap gap-x-3 gap-y-1">
          <CopyValue value={domain.hostname} />
          <CopyValue value={v.cname_target} />
        </span>
      </div>
    </div>
  );
}

function DomainsTab({ site }: { site: Site }) {
  const { can, orgId } = useOrganization();
  const qc = useQueryClient();
  const [hostname, setHostname] = useState("");
  const domains = useQuery({
    queryKey: siteKeys.domains(orgId, site.id),
    queryFn: () => portal.domains(site.id),
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: siteKeys.domains(orgId, site.id) });
    void qc.invalidateQueries({ queryKey: ["overview", orgId] });
    void qc.invalidateQueries({ queryKey: ["org-domains", orgId] });
  };
  const add = useMutation({
    mutationFn: () => portal.addDomain(site.id, hostname.trim()),
    onSuccess: () => {
      setHostname("");
      toast.show({ tone: "success", title: "Domain added" });
      refresh();
    },
  });
  const verify = useMutation({ mutationFn: portal.verifyDomain, onSuccess: refresh });
  const remove = useMutation({ mutationFn: portal.removeDomain, onSuccess: refresh });
  const toast = useToast();
  const [disconnecting, setDisconnecting] = useState<CustomDomain | null>(null);
  // Disconnected domains still inside the undo window: hidden here, removed when their timer ends.
  const [held, setHeld] = useState<string[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const listRef = useRef<HTMLDivElement>(null);
  const release = (id: string) => setHeld((h) => h.filter((x) => x !== id));
  const disconnect = (d: CustomDomain) => {
    setDisconnecting(null);
    setHeld((h) => [...h, d.id]);
    timers.current.set(
      d.id,
      setTimeout(() => {
        timers.current.delete(d.id);
        remove.mutate(d.id, { onError: () => release(d.id) });
      }, UNDO_DISCONNECT_MS),
    );
    toast.show({
      id: `disconnect-${d.id}`,
      title: `${d.hostname} disconnected`,
      body: `Undo within ${UNDO_DISCONNECT_MS / 1000} seconds to keep it connected.`,
      duration: UNDO_DISCONNECT_MS,
      action: {
        label: "Undo",
        onClick: () => {
          clearTimeout(timers.current.get(d.id));
          timers.current.delete(d.id);
          release(d.id);
        },
      },
    });
  };
  // Leaving the page ends the undo window: what is still waiting is disconnected now, not forgotten.
  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const [id, timer] of pending) {
        clearTimeout(timer);
        toast.dismiss(`disconnect-${id}`);
        void portal.removeDomain(id).finally(() => {
          void qc.invalidateQueries({ queryKey: siteKeys.domains(orgId, site.id) });
          void qc.invalidateQueries({ queryKey: ["overview", orgId] });
          void qc.invalidateQueries({ queryKey: ["org-domains", orgId] });
        });
      }
      pending.clear();
    };
  }, [toast, qc, orgId, site.id]);
  const shown = domains.data?.domains.filter((d) => !held.includes(d.id)) ?? [];
  const canManage = can("domains:manage");
  const limitReached = add.error instanceof ApiError && add.error.reason === "PLAN_LIMIT_REACHED";

  return (
    <Card>
      <CardHeader
        title={
          <>
            Custom domains <HelpHint helpKey={HELP_KEYS.websites.customDomains} label="custom domains" />
          </>
        }
        description={`Your site is always available at ${site.hostname}. Connect your own domain too.`}
        action={
          <LinkButton
            to="/domains/find"
            requires="domains:manage"
            className="text-sm font-medium text-brand-700 hover:underline"
          >
            Buy a domain →
          </LinkButton>
        }
      />
      {canManage && (
        <Form
          className="flex flex-wrap items-start gap-2 border-b border-border px-5 py-4"
          summaryClassName="w-full"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          <div className="min-w-60 flex-1">
            <Input
              aria-label="Domain name"
              name="hostname"
              placeholder="www.example.com"
              value={hostname}
              onChange={(e) => setHostname(e.target.value)}
              required
              maxLength={253}
            />
          </div>
          <Button type="submit" loading={add.isPending}>
            Connect domain
          </Button>
          {add.error && (
            <div className="w-full">
              {limitReached ? (
                <Alert tone="warning">
                  {add.error.message}{" "}
                  <Link to="/billing" className="font-medium underline">
                    See plans
                  </Link>
                </Alert>
              ) : (
                <ErrorAlert
                  error={
                    add.error instanceof ApiError && add.error.fieldError("hostname")
                      ? new Error(add.error.fieldError("hostname"))
                      : add.error
                  }
                />
              )}
            </div>
          )}
        </Form>
      )}
      {domains.isPending ? (
        <FullPageSpinner />
      ) : domains.isError ? (
        <ErrorState error={domains.error} />
      ) : (
        // Takes focus after a disconnect: the row whose button opened the dialog has gone.
        <div ref={listRef} role="group" tabIndex={-1} aria-label="Custom domains" className="outline-none">
          {shown.length === 0 ? (
            <EmptyState icon={<Globe className="size-5" />} title="No custom domains" />
          ) : (
            <ul className="divide-y divide-border">
              {shown.map((d) => (
                <li key={d.id} className="px-5 py-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <a
                      href={siteHref(d.hostname, site.link_scheme)}
                      target="_blank"
                      rel="noreferrer"
                      className="min-w-0 flex-1 truncate text-sm font-medium text-fg hover:underline"
                    >
                      {d.hostname}
                    </a>
                    <DomainStatusBadge status={d.status} />
                    {d.status !== "active" && (
                      <Button
                        size="sm"
                        variant="secondary"
                        requires="domains:manage"
                        loading={verify.isPending && verify.variables === d.id}
                        onClick={() => verify.mutate(d.id)}
                      >
                        Check DNS
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      requires="domains:manage"
                      aria-label={`Remove ${d.hostname}`}
                      loading={remove.isPending && remove.variables === d.id}
                      onClick={() => setDisconnecting(d)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                  {d.check_error && <p className="mt-1 text-xs text-red-600">{d.check_error}</p>}
                  {d.status !== "active" && <DnsInstructions domain={d} />}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {disconnecting && (
        <DisconnectDomainDialog
          domain={disconnecting}
          site={site}
          stays={shown.filter((d) => d.id !== disconnecting.id && d.status === "active")}
          onConfirm={() => {
            disconnect(disconnecting);
            requestAnimationFrame(() => listRef.current?.focus());
          }}
          onClose={() => setDisconnecting(null)}
        />
      )}
      {(verify.error || remove.error) && (
        <div className="px-5 pb-4">
          <ErrorAlert error={verify.error ?? remove.error} />
        </div>
      )}
    </Card>
  );
}

/** What disconnecting a domain does: which site stops answering there, and where it still answers. */
function DisconnectDomainDialog({
  domain,
  site,
  stays,
  onConfirm,
  onClose,
}: {
  domain: CustomDomain;
  site: Site;
  /** The site's other connected domains, which keep working. */
  stays: CustomDomain[];
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal
      open
      onClose={onClose}
      title={`Disconnect ${domain.hostname}?`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm}>
            Disconnect
          </Button>
        </>
      }
    >
      <p className="text-sm text-fg-muted">
        Visitors to <strong className="text-fg">{domain.hostname}</strong> will no longer reach{" "}
        <strong className="text-fg">{site.name}</strong>. Connecting it again later means checking its DNS
        again.
      </p>
      <div>
        <p className="text-xs font-semibold tracking-wide text-fg-subtle uppercase">Site affected</p>
        <ul className="mt-2 rounded-lg border border-border px-3 py-2 text-sm">
          <li>
            <span className="font-medium text-fg">{site.name}</span>
            <span className="block text-xs text-fg-subtle">
              Still reachable at {[site.hostname, ...stays.map((d) => d.hostname)].join(", ")}
            </span>
          </li>
        </ul>
      </div>
      <p className="text-xs text-fg-subtle">
        You can undo this for {UNDO_DISCONNECT_MS / 1000} seconds after disconnecting.
      </p>
    </Modal>
  );
}

function SettingsTab({ site }: { site: Site }) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const cannotRename = useMissingPermission({ permission: "websites:update", on: ["site", site.id] });
  const [name, setName] = useState(site.name);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const toast = useToast();
  const rename = useMutation({
    mutationFn: () => portal.renameSite(site.id, name.trim()),
    onSuccess: (updated) => {
      toast.show({ tone: "success", title: "Website renamed" });
      qc.setQueryData(siteKeys.site(orgId, site.id), updated);
      void qc.invalidateQueries({ queryKey: ["sites", orgId] });
    },
  });
  const grace = useDeletionGrace();
  const del = useDeleteSite(site, async () => {
    await qc.invalidateQueries({ queryKey: ["sites", orgId] });
    await qc.invalidateQueries({ queryKey: ["overview", orgId] });
    navigate("/websites", { replace: true });
  });
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader title="General" />
        <Form
          className="space-y-4 px-5 py-4"
          error={rename.error}
          onSubmit={(e) => {
            e.preventDefault();
            rename.mutate();
          }}
        >
          <ErrorAlert error={rename.error} />
          {rename.isSuccess && <Alert tone="success">Saved.</Alert>}
          <Field label="Website name" name="name" required={!cannotRename} hint={cannotRename ?? undefined}>
            {(p) => (
              <Input
                {...p}
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={100}
                disabled={!!cannotRename}
              />
            )}
          </Field>
          <Button
            type="submit"
            requires={{ permission: "websites:update", on: ["site", site.id] }}
            loading={rename.isPending}
            disabledReason={name.trim() === site.name ? "Change the name to save." : undefined}
          >
            Save
          </Button>
        </Form>
      </Card>

      {site.status !== "pending_deletion" && (
        <Card className="border-red-500/40">
          <CardHeader
            title="Delete website"
            description={
              grace.days > 0
                ? `Takes the site offline and disconnects its domains. It is kept for ${grace.days} ${grace.days === 1 ? "day" : "days"}, so you can restore it until it is permanently deleted.`
                : "Takes the site offline, disconnects its domains and deletes its deployments. This cannot be undone."
            }
            action={
              <Button
                variant="danger"
                requires={{ permission: "websites:delete", on: ["site", site.id] }}
                onClick={() => setConfirmOpen(true)}
              >
                Delete website
              </Button>
            }
          />
        </Card>
      )}

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Delete website?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={del.isPending}
              disabledReason={
                confirmText !== site.name
                  ? "Type the website's name to confirm."
                  : grace.loading
                    ? "Checking how long a deleted website is kept…"
                    : undefined
              }
              onClick={() => del.mutate()}
            >
              {grace.days > 0 ? "Delete website" : "Delete permanently"}
            </Button>
          </>
        }
      >
        <ErrorAlert error={del.error} />
        {confirmOpen && <DeleteEffect subject={site.name} />}
        <p className="text-sm text-fg-muted">
          Type <strong className="text-fg">{site.name}</strong> to confirm.
        </p>
        <Input
          aria-label="Website name"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          // eslint-disable-next-line jsx-a11y/no-autofocus -- the form is this screen's only task, so focus starts in its first field (PHP-3348)
          autoFocus
        />
      </Modal>
    </div>
  );
}
